import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import swal from 'sweetalert';
import { Modal } from 'react-bootstrap';
import Sidebar_Progress from '../../../layouts/Operational_Sidebar_Progress.component';
import ManagerLoginHeader from '../ManagerLoginHeader.component';
import Loader from '../../Employee/Loader.component';
import { getStoredPermissions } from '../../../../utils/permissions';
import {
  persistSubscriptionAccess,
  isSubscriptionAccessBlocked,
  resolvePostActivationPath,
} from '../../../../utils/subscriptionAccess';
import { API_URL } from '../../../../constant/API_Settings';
import { formatReportDate } from '../../../../utils/reportFormat';
import {
  fetchCheckoutSummary,
  fetchGstProfile,
  fetchInvoicePrintUrl,
  fetchSubscriptionHistory,
  fetchSubscriptionInvoices,
  fetchSubscriptionOverview,
  fetchSubscriptionPayments,
  formatCurrency,
  initiateInvoiceOnlinePayment,
  initiateSubscriptionPayment,
  loadRazorpayScript,
  normalizeCouponCode,
  previewSubscriptionCoupon,
  quotePlanPurchase,
  BILLING_MONTH_OPTIONS,
  saveGstProfile,
  saveSubscriptionAutoBilling,
  submitCcavenueForm,
  submitPayUForm,
  verifyRazorpayPayment,
} from './subscriptionApi';
import GstDetailsForm from './GstDetailsForm';
import GstLocationFields from '../../shared/GstLocationFields';
import {
  DEFAULT_INVOICE_SETTINGS,
  applyInvoiceFieldUpdates,
  buildGstSavePayload,
  gstinSideEffects,
  mapInvoiceSettingsFromApi,
  stateNameByCode,
  validateGstProfile,
} from '../../../../utils/invoiceSettingsForm';
import './SubscriptionManagement.css';

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'fa-dashboard' },
  { id: 'purchase', label: 'Purchase', icon: 'fa-shopping-cart' },
  { id: 'payments', label: 'Payment History', icon: 'fa-credit-card' },
  { id: 'invoices', label: 'Invoices', icon: 'fa-file-text-o' },
  { id: 'gst', label: 'GST details', icon: 'fa-id-card' },
];

/** Empty / 0 → Unlimited for plan package limits. */
function formatPlanLimit(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n.toLocaleString('en-IN') : 'Unlimited';
}

function isFreePlan(plan) {
  return Boolean(plan?.is_trial || plan?.is_free || Number(plan?.amount) <= 0);
}

function PlanLimitsList({ plan }) {
  if (!plan) return null;
  const items = [
    { label: 'Nozzles', value: plan.max_nozzles },
    { label: 'Monthly transactions', value: plan.max_monthly_transactions },
    { label: 'Employees', value: plan.max_employees },
    { label: 'Tanks', value: plan.max_tanks },
  ];
  return (
    <ul className="sub-mgmt-plan-limits">
      {items.map((item) => (
        <li key={item.label}>
          <span>{item.label}</span>
          <strong>{formatPlanLimit(item.value)}</strong>
        </li>
      ))}
    </ul>
  );
}

function StatusBadge({ active }) {
  return active
    ? <span className="sub-mgmt-badge-active">Active</span>
    : <span className="sub-mgmt-badge-inactive">Inactive</span>;
}

function paymentStatusClass(label) {
  if (label === 'Success') return 'text-success fw-semibold';
  if (label === 'Initiated') return 'text-warning fw-semibold';
  return 'text-danger fw-semibold';
}

const SubscriptionManagementConfig = (props) => {
  const history = useHistory();
  const permissions = getStoredPermissions();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState('');
  const [paying, setPaying] = useState(false);
  const [billing, setBilling] = useState({
    billing_name: '',
    billing_address: '',
    billing_state: '',
    billing_state_code: '',
    billing_district: '',
    billing_gstin: '',
    billing_country: 'India',
    billing_tel: '',
    billing_email: '',
    plan_id: '',
    amount: '',
  });
  const [gstForm, setGstForm] = useState(DEFAULT_INVOICE_SETTINGS);
  const [gstErrors, setGstErrors] = useState({});
  const [gstSaving, setGstSaving] = useState(false);
  const [payingInvoiceId, setPayingInvoiceId] = useState(null);
  const [billingMonths, setBillingMonths] = useState(1);
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [serverCheckoutQuote, setServerCheckoutQuote] = useState(null);
  const [autoDebitCheckout, setAutoDebitCheckout] = useState(false);
  const [savingAutoBilling, setSavingAutoBilling] = useState(false);

  const loadTabData = useCallback(async (tab) => {
    setLoading(true);
    try {
      if (tab === 'overview' || tab === 'purchase') {
        const data = await fetchSubscriptionOverview();
        setOverview(data);
        if (data.access) {
          persistSubscriptionAccess({
            access: data.access,
            subscription_blocked: data.access.blocked,
            subscription_expired: data.access.show_renew_notice,
            subscription_needs_plan: data.access.show_subscribe_notice,
            subscription_end_date: data.access.expired_end_date,
          });
        }
        if (data.gateways?.length) {
          setSelectedGateway((prev) => prev || data.gateways[0].id);
        }
        if (data.auto_billing) {
          setAutoDebitCheckout(Boolean(data.auto_billing.auto_debit));
          if (data.auto_billing.billing_months) {
            setBillingMonths(data.auto_billing.billing_months);
          }
        }
      }
      if (tab === 'overview') {
        const hist = await fetchSubscriptionHistory();
        setHistoryRows(hist.subscriptions || []);
      }
      if (tab === 'payments') {
        const pay = await fetchSubscriptionPayments();
        setPayments(pay.payments || []);
      }
      if (tab === 'invoices') {
        const inv = await fetchSubscriptionInvoices();
        setInvoices(inv.invoices || []);
      }
      if (tab === 'gst') {
        const gst = await fetchGstProfile();
        if (gst.status && gst.data) {
          setGstForm(mapInvoiceSettingsFromApi(gst.data));
          setGstErrors({});
        }
      }
    } catch (e) {
      swal('Error', e.message || 'Unable to load subscription data.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const [ready, setReady] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && TABS.some((item) => item.id === tab)) {
      setActiveTab(tab);
    }

    const requestOptions = {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('AUTH_TOKEN') || ''}`,
        Accept: 'application/json',
      },
      body: JSON.stringify({ pathurl: window.location.pathname }),
    };
    fetch(`${API_URL}/check_url`, requestOptions)
      .then((r) => r.json())
      .then((data) => {
        if (data.allowurl === 1) {
          if (data.url) {
            const target = `/${String(data.url).replace(/^\//, '')}`;
            const current = `/${String(window.location.pathname).replace(/^\//, '').split('/')[0]}`;
            if (target !== current) {
              history.push(target);
              return;
            }
          }
          setReady(true);
        } else {
          history.goBack();
        }
      })
      .catch(() => history.goBack());
  }, [history]);

  useEffect(() => {
    if (ready) {
      loadTabData(activeTab);
    }
  }, [ready, activeTab, loadTabData]);

  function switchTab(tabId) {
    setActiveTab(tabId);
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tabId);
    history.replace(`/subscription-management?${params.toString()}`);
  }

  async function openCheckout(plan) {
    if (!isFreePlan(plan) && !gateways.length) {
      swal(
        'Payment gateway not configured',
        'Add RAZORPAY_KEY / RAZORPAY_SECRET or PAYU_MERCHANT_KEY / PAYU_SALT in the backend .env file, then restart the server.',
        'warning'
      );
      return;
    }

    try {
      const summary = await fetchCheckoutSummary(plan.id);
      const pump = summary.pump || {};
      const stateCode = pump.pump_state_code || '';
      setSelectedPlan(plan);
      setBillingMonths(overview?.auto_billing?.billing_months || 1);
      setCouponInput('');
      setAppliedCoupon(null);
      setCouponMessage('');
      setServerCheckoutQuote(null);
      setAutoDebitCheckout(Boolean(overview?.auto_billing?.auto_debit));
      setBilling({
        billing_name: pump.owner_name || pump.pump_name || '',
        billing_address: pump.invoice_address || pump.pump_address || '',
        billing_state: pump.pump_state_name || stateNameByCode(stateCode) || '',
        billing_state_code: stateCode,
        billing_district: pump.pump_district || '',
        billing_gstin: pump.gst_no || '',
        billing_country: 'India',
        billing_tel: pump.pump_phone || '',
        billing_email: pump.pump_email || '',
        plan_id: plan.id,
        amount: plan.amount,
      });
      setCheckoutOpen(true);
    } catch (e) {
      swal('Error', e.message || 'Unable to load checkout details.', 'error');
    }
  }

  function handleBillingChange(event) {
    const { name, value } = event.target;
    if (name === 'billing_gstin') {
      const next = gstinSideEffects(value, {
        pump_state_code: billing.billing_state_code,
      });
      setBilling((prev) => ({
        ...prev,
        billing_gstin: next.gst_no,
        billing_state_code: next.pump_state_code || prev.billing_state_code,
        billing_state: next.pump_state_name || prev.billing_state,
        billing_district: next.pump_district !== undefined ? next.pump_district : prev.billing_district,
      }));
      return;
    }
    setBilling((prev) => ({ ...prev, [name]: value }));
  }

  function handleCheckoutLocationChange(name, value) {
    const mapped = {
      pump_state_code: 'billing_state_code',
      pump_state_name: 'billing_state',
      pump_district: 'billing_district',
      place: 'billing_state',
    };
    const billingKey = mapped[name];
    if (!billingKey) {
      return;
    }
    setBilling((prev) => ({ ...prev, [billingKey]: value }));
  }

  function handleGstFieldChange(nameOrPatch, value) {
    setGstForm((prev) => applyInvoiceFieldUpdates(prev, nameOrPatch, value));
    setGstErrors((prev) => {
      if (nameOrPatch && typeof nameOrPatch === 'object' && !Array.isArray(nameOrPatch)) {
        const next = { ...prev };
        Object.keys(nameOrPatch).forEach((key) => {
          next[key] = null;
        });
        return next;
      }
      return { ...prev, [nameOrPatch]: null };
    });
  }

  async function handleGstSubmit(event) {
    event.preventDefault();
    const payload = buildGstSavePayload(gstForm);
    const validationErrors = validateGstProfile(payload);
    setGstErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setGstSaving(true);
    try {
      const result = await saveGstProfile(payload);
      if (result.status) {
        swal('Saved', 'GST details saved. They will be used on subscription invoices.', 'success');
        const gst = await fetchGstProfile();
        if (gst.status && gst.data) {
          setGstForm(mapInvoiceSettingsFromApi(gst.data));
        }
      } else {
        const message = result.message
          || (result.errors && Object.values(result.errors).flat().join(', '))
          || 'Unable to save GST details.';
        swal('GST details', message, 'warning');
      }
    } catch (e) {
      swal('Error', e.message || 'Unable to save GST details.', 'error');
    } finally {
      setGstSaving(false);
    }
  }

  function validateBilling() {
    if (!billing.billing_name?.trim()) return 'Billing name is required';
    if (!billing.billing_email?.trim()) return 'Email is required';
    if (!billing.billing_tel?.trim()) return 'Phone number is required';
    if (!isFreePlan(selectedPlan) && !selectedGateway) return 'Select a payment gateway';
    if (!isFreePlan(selectedPlan)) {
      const months = parseInt(billingMonths, 10);
      if (!Number.isFinite(months) || months < 1 || months > 36) return 'Select between 1 and 36 months';
    }
    return null;
  }

  async function handlePay() {
    if (!selectedPlan) return;

    const validationError = validateBilling();
    if (validationError) {
      swal('Checkout', validationError, 'warning');
      return;
    }

    setPaying(true);
    try {
      if (billing.billing_gstin) {
        try {
          await saveGstProfile(buildGstSavePayload({
            ...gstForm,
            dealer: billing.billing_name,
            invoice_address: billing.billing_address || gstForm.invoice_address,
            pump_address: billing.billing_address || gstForm.pump_address,
            pump_phone: billing.billing_tel,
            pump_email: billing.billing_email,
            gst_no: billing.billing_gstin,
            invoice_gst_enabled: true,
            pump_state_code: billing.billing_state_code,
            pump_state_name: billing.billing_state,
            pump_district: billing.billing_district,
          }));
        } catch (e) {
          // Checkout payment should still proceed if GST save is incomplete.
        }
      }

      const payload = {
        ...billing,
        billing_state: billing.billing_state || stateNameByCode(billing.billing_state_code) || '',
        plan_id: selectedPlan.id,
        gateway: selectedGateway,
        billing_months: billingMonths,
        auto_debit: autoDebitCheckout,
        coupon_code: appliedCoupon?.code || '',
      };
      const result = await initiateSubscriptionPayment(payload);
      if (!result.status) {
        swal('Payment', result.message || 'Unable to start payment.', 'error');
        return;
      }

      if (result.gateway === 'free' || result.free_plan) {
        persistSubscriptionAccess(result.access || result);
        setCheckoutOpen(false);
        swal('Success', result.message || 'Free trial activated.', 'success').then(() => {
          history.push(resolvePostActivationPath(result.resume_screen, '/dashboard'));
        });
        return;
      }

      if (result.gateway === 'razorpay') {
        const Razorpay = await loadRazorpayScript();
        const options = {
          key: result.key,
          amount: result.amount,
          currency: result.currency,
          name: 'Petrosmart',
          description: result.description || result.plan_name,
          order_id: result.razorpay_order_id,
          prefill: result.prefill,
          theme: { color: '#1a4fd6' },
          ...(result.customer_id ? { customer_id: result.customer_id } : {}),
          ...(result.save ? { save: 1 } : {}),
          handler: async (response) => {
            try {
              const verified = await verifyRazorpayPayment({
                order_id: result.order_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              if (verified.status) {
                setCheckoutOpen(false);
                swal('Success', verified.message, 'success').then(() => {
                  history.push(`/payment-Success?order_id=${result.order_id}`);
                });
              } else {
                swal('Failed', verified.message || 'Verification failed.', 'error');
              }
            } catch (err) {
              swal('Failed', err.message || 'Verification failed.', 'error');
            } finally {
              setPaying(false);
            }
          },
          modal: {
            ondismiss: () => setPaying(false),
          },
        };
        const rzp = new Razorpay(options);
        rzp.on('payment.failed', (resp) => {
          swal('Failed', resp.error?.description || 'Payment was not completed.', 'error');
          setPaying(false);
        });
        rzp.open();
        return;
      }

      if (result.gateway === 'payu') {
        setCheckoutOpen(false);
        submitPayUForm(result.action, result.fields);
        return;
      }

      if (result.gateway === 'ccavenue') {
        setCheckoutOpen(false);
        submitCcavenueForm(result.action, result.encrypted_data, result.access_code);
      }
    } catch (e) {
      swal('Error', e.message || 'Payment could not be started.', 'error');
    } finally {
      setPaying(false);
    }
  }

  async function downloadInvoice(row) {
    try {
      const url = row.print_url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      const res = await fetchInvoicePrintUrl(row.id);
      if (res.status && res.url) {
        window.open(res.url, '_blank', 'noopener,noreferrer');
      } else {
        swal('Error', res.message || 'Unable to open invoice.', 'error');
      }
    } catch (e) {
      swal('Error', e.message || 'Unable to download invoice.', 'error');
    }
  }

  async function payPendingInvoice(row) {
    if (!row?.id || payingInvoiceId) {
      return;
    }
    setPayingInvoiceId(row.id);
    try {
      const result = await initiateInvoiceOnlinePayment(row.id, {
        billing_name: billing.billing_name,
        billing_email: billing.billing_email,
        billing_tel: billing.billing_tel,
        billing_address: billing.billing_address,
        billing_state: billing.billing_state,
        billing_country: billing.billing_country || 'India',
      });
      if (!result.status) {
        swal('Payment', result.message || 'Unable to start payment.', 'error');
        return;
      }

      const Razorpay = await loadRazorpayScript();
      await new Promise((resolve, reject) => {
        const rzp = new Razorpay({
          key: result.key,
          amount: result.amount,
          currency: result.currency,
          name: 'Petrosmart',
          description: result.description || result.plan_name || `Invoice #${row.id}`,
          order_id: result.razorpay_order_id,
          prefill: result.prefill,
          theme: { color: '#1a4fd6' },
          handler: async (response) => {
            try {
              const verified = await verifyRazorpayPayment({
                order_id: result.order_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              if (verified.status) {
                swal('Success', verified.message || 'Payment successful.', 'success');
                const [overviewData, inv] = await Promise.all([
                  fetchSubscriptionOverview(),
                  fetchSubscriptionInvoices(),
                ]);
                setOverview(overviewData);
                setInvoices(inv.invoices || []);
                resolve(verified);
              } else {
                reject(new Error(verified.message || 'Verification failed.'));
              }
            } catch (err) {
              reject(err);
            }
          },
          modal: {
            ondismiss: () => reject(new Error('Payment cancelled')),
          },
        });
        rzp.on('payment.failed', (resp) => {
          reject(new Error(resp.error?.description || 'Payment was not completed.'));
        });
        rzp.open();
      });
    } catch (e) {
      if (e.message !== 'Payment cancelled') {
        swal('Error', e.message || 'Payment could not be completed.', 'error');
      }
    } finally {
      setPayingInvoiceId(null);
    }
  }

  async function handleAutoBillingChange(patch) {
    const current = overview?.auto_billing || {};
    const next = {
      auto_renew: current.auto_renew,
      auto_debit: current.auto_debit,
      billing_months: current.billing_months || 1,
      ...patch,
    };
    if (patch.auto_renew === false) {
      next.auto_renew = false;
      next.auto_debit = false;
    } else if (next.auto_debit) {
      next.auto_renew = true;
    }

    setSavingAutoBilling(true);
    try {
      const result = await saveSubscriptionAutoBilling(next);
      if (!result.status) {
        swal('Auto billing', result.message || 'Unable to save auto billing.', 'warning');
        return;
      }
      setOverview((prev) => ({ ...prev, auto_billing: result.auto_billing }));
      setAutoDebitCheckout(Boolean(result.auto_billing?.auto_debit));
      if (result.message) {
        swal('Saved', result.message, result.auto_billing?.auto_debit && !result.auto_billing?.mandate_ready ? 'info' : 'success');
      }
    } catch (e) {
      swal('Error', e.message || 'Unable to save auto billing.', 'error');
    } finally {
      setSavingAutoBilling(false);
    }
  }
  const current = overview?.current;
  const plans = overview?.plans || [];
  const gateways = overview?.gateways || [];
  const outstandingInvoices = overview?.outstanding_invoices || [];
  const couponMonthsOk = !appliedCoupon || Number(billingMonths) >= Number(appliedCoupon.min_months || 1);
  // Always compute amounts locally from discount_months so a stale/old API cannot show a flat full-term %.
  // Server preview is still used to validate the code and refresh coupon metadata.
  const checkoutQuote = selectedPlan
    ? quotePlanPurchase(
      selectedPlan,
      billingMonths,
      couponMonthsOk
        ? (serverCheckoutQuote?.coupon && String(serverCheckoutQuote.coupon.code || '').toUpperCase() === String(appliedCoupon?.code || '').toUpperCase()
          ? serverCheckoutQuote.coupon
          : appliedCoupon)
        : null
    )
    : null;

  async function applyCheckoutCoupon(codeOverride = null) {
    if (!selectedPlan || isFreePlan(selectedPlan)) return;
    // Ignore click/keyboard events — only accept a real string override.
    const raw = (typeof codeOverride === 'string' || typeof codeOverride === 'number')
      ? codeOverride
      : (couponInput || appliedCoupon?.code || '');
    const code = normalizeCouponCode(raw);
    if (!code) {
      setCouponMessage('Enter a coupon code.');
      return;
    }
    setCouponInput(code);
    setCouponApplying(true);
    setCouponMessage('');
    try {
      const months = Math.min(36, Math.max(1, parseInt(billingMonths, 10) || 1));
      const result = await previewSubscriptionCoupon({
        plan_id: selectedPlan.id,
        months,
        billing_months: months,
        coupon_code: code,
      });
      if (!result.status) {
        setAppliedCoupon(null);
        setServerCheckoutQuote(null);
        setCouponMessage(result.message || 'This coupon code is not valid.');
        return;
      }
      setAppliedCoupon(result.coupon);
      setServerCheckoutQuote(result.quote || null);
      setCouponInput(result.coupon?.code || code);
      setCouponMessage(result.message || 'Coupon applied.');
    } catch (e) {
      setAppliedCoupon(null);
      setServerCheckoutQuote(null);
      setCouponMessage(e.message || 'This coupon code is not valid.');
    } finally {
      setCouponApplying(false);
    }
  }

  function removeCheckoutCoupon() {
    setAppliedCoupon(null);
    setServerCheckoutQuote(null);
    setCouponInput('');
    setCouponMessage('');
  }

  function changeBillingMonths(nextMonths) {
    setBillingMonths(nextMonths);
    setServerCheckoutQuote(null);
  }

  // Re-check coupon only when billing months change — not when Apply sets appliedCoupon
  // (that was causing a second preview call on every successful apply).
  useEffect(() => {
    if (!checkoutOpen || !appliedCoupon?.code || !selectedPlan || isFreePlan(selectedPlan)) {
      return undefined;
    }
    const code = normalizeCouponCode(appliedCoupon.code);
    if (!code) {
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const months = Math.min(36, Math.max(1, parseInt(billingMonths, 10) || 1));
        const result = await previewSubscriptionCoupon({
          plan_id: selectedPlan.id,
          months,
          billing_months: months,
          coupon_code: code,
        });
        if (cancelled) return;
        if (!result.status) {
          setAppliedCoupon(null);
          setServerCheckoutQuote(null);
          setCouponMessage(result.message || 'Invalid coupon code.');
          return;
        }
        setAppliedCoupon(result.coupon);
        setServerCheckoutQuote(result.quote || null);
        setCouponMessage(result.message || 'Coupon applied.');
      } catch (e) {
        if (cancelled) return;
        setAppliedCoupon(null);
        setServerCheckoutQuote(null);
        setCouponMessage(e.message || 'Invalid coupon code.');
      }
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only revalidate when months change
  }, [billingMonths]);

  function renderGatewayBanner() {
    const hasFreePlan = plans.some((plan) => isFreePlan(plan));
    if (gateways.length > 0 || hasFreePlan) return null;
    return (
      <div className="sub-mgmt-alert">
        <strong>Payment gateways are not configured.</strong>
        <p className="mb-0 mt-2 small">
          Add keys in <code>backend/.env</code>: <code>RAZORPAY_KEY</code>, <code>RAZORPAY_SECRET</code>,
          or <code>PAYU_MERCHANT_KEY</code>, <code>PAYU_SALT</code>, then restart the Laravel server.
          Paid subscribe buttons stay unavailable until at least one gateway is active. A ₹0 free trial can still be started.
        </p>
      </div>
    );
  }

  function renderOutstandingBanner() {
    if (outstandingInvoices.length === 0) {
      return null;
    }
    return (
      <div className="sub-mgmt-alert sub-mgmt-pending-banner">
        <strong>Unpaid subscription invoice{outstandingInvoices.length > 1 ? 's' : ''}</strong>
        <p className="mb-2 mt-2 small">
          Outstanding amount: <strong>{formatCurrency(overview?.outstanding_amount)}</strong>.
          Pay online with Razorpay to clear the pending balance.
        </p>
        <div className="d-flex flex-wrap gap-2">
          {outstandingInvoices.map((row) => (
            <button
              key={row.id}
              type="button"
              className="btn btn-sm btn-warning fw-semibold"
              disabled={!!payingInvoiceId}
              onClick={() => payPendingInvoice(row)}
            >
              <i className="fa fa-credit-card me-1" />
              {payingInvoiceId === row.id
                ? 'Opening Razorpay…'
                : `Pay invoice #${row.id} (${formatCurrency(row.amount_pending)})`}
            </button>
          ))}
        </div>
      </div>
    );
  }
  function renderAutoBillingCard() {
    const billing = overview?.auto_billing || {};
    const razorpayReady = Boolean(billing.razorpay_configured);
    return (
      <div className="sub-mgmt-auto-card">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
          <div>
            <h6 className="mb-1">Auto debit &amp; auto renewal</h6>
            <p className="text-muted small mb-0">
              When auto debit collects a payment, Petrosmart renews the account from the previous end date.
            </p>
          </div>
          <span className={`sub-mgmt-auto-pill sub-mgmt-auto-pill--${billing.status || 'off'}`}>
            {billing.status === 'armed' && 'Auto debit armed'}
            {billing.status === 'needs_mandate' && 'Waiting for Razorpay method'}
            {billing.status === 'renew_only' && 'Auto renewal on'}
            {(!billing.status || billing.status === 'off') && 'Off'}
          </span>
        </div>

        <label className="sub-mgmt-switch">
          <input
            type="checkbox"
            checked={Boolean(billing.auto_renew)}
            disabled={savingAutoBilling}
            onChange={(e) => handleAutoBillingChange({ auto_renew: e.target.checked })}
          />
          <span>Auto renewal</span>
          <small>Successful payments extend the plan automatically.</small>
        </label>

        <label className="sub-mgmt-switch">
          <input
            type="checkbox"
            checked={Boolean(billing.auto_debit)}
            disabled={savingAutoBilling || !razorpayReady}
            onChange={(e) => handleAutoBillingChange({ auto_debit: e.target.checked, auto_renew: e.target.checked || billing.auto_renew })}
          />
          <span>Auto debit</span>
          <small>
            {razorpayReady
              ? (billing.mandate_ready
                ? `Saved method: ${billing.payment_method || 'Razorpay'}. Charge on ${billing.next_debit_on || 'the end date'}.`
                : 'Turn on, then pay once with Razorpay to save the card / UPI AutoPay.')
              : 'Requires Razorpay keys in backend .env'}
          </small>
        </label>

        {billing.auto_debit && (
          <div className="d-flex align-items-center gap-2 mt-2">
            <label className="small text-muted mb-0" htmlFor="auto-billing-months">Renew for</label>
            <select
              id="auto-billing-months"
              className="form-select form-select-sm"
              style={{ width: 140 }}
              disabled={savingAutoBilling}
              value={billing.billing_months || 1}
              onChange={(e) => handleAutoBillingChange({ billing_months: parseInt(e.target.value, 10) || 1 })}
            >
              {BILLING_MONTH_OPTIONS.map((months) => (
                <option key={months} value={months}>{months} {months === 1 ? 'month' : 'months'}</option>
              ))}
            </select>
          </div>
        )}

        {billing.last_auto_debit_status === 'failed' && billing.last_auto_debit_error && (
          <p className="text-danger small mb-0 mt-2">Last auto debit: {billing.last_auto_debit_error}</p>
        )}
        {billing.last_auto_debit_status === 'success' && billing.last_auto_debit_at && (
          <p className="text-success small mb-0 mt-2">Last auto debit: {billing.last_auto_debit_at}</p>
        )}
      </div>
    );
  }

  function renderOverview() {
    const gated = Boolean(overview?.access?.blocked || isSubscriptionAccessBlocked());
    return (
      <>
        {gated && !overview?.is_active && (
          <div className="sub-mgmt-onboard-banner">
            <h3>Subscribe to start using Petrosmart</h3>
            <p>
              Choose a plan, including the free trial if it is available. After the trial ends you
              must take a paid package or ask an administrator to add a subscription. Paid plans that
              later expire do not lock you out — you will see a renew reminder in the menu.
            </p>
            <button type="button" className="btn btn-light btn-sm fw-semibold" onClick={() => switchTab('purchase')}>
              <i className="fa fa-shopping-cart me-1" /> Choose a plan
            </button>
          </div>
        )}
        {overview?.access?.show_renew_notice && (
          <div className="sub-mgmt-alert">
            <strong>Your subscription ended. Please renew.</strong>
            <p className="mb-0 mt-2 small">
              Access is still open. Renewal starts from {overview.access.expired_end_date || 'the previous end date'}, not from today.
            </p>
          </div>
        )}
        {renderOutstandingBanner()}
        <div className="sub-mgmt-hero">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h2>Your Petrosmart plan</h2>
              <p>{current ? current.plan_name : 'No active subscription — choose a plan to get started'}</p>
            </div>
            <StatusBadge active={overview?.is_active} />
          </div>
          {current && (
            <div className="sub-mgmt-hero-stats">
              <div className="sub-mgmt-stat">
                <label>Start date</label>
                <strong>{current.subscription_start_date}</strong>
              </div>
              <div className="sub-mgmt-stat">
                <label>End date</label>
                <strong>{current.subscription_end_date}</strong>
              </div>
              <div className="sub-mgmt-stat">
                <label>Amount</label>
                <strong>{formatCurrency(current.amount)}</strong>
              </div>
              <div className="sub-mgmt-stat">
                <label>Nozzles</label>
                <strong>{formatPlanLimit(current.max_nozzles)}</strong>
              </div>
              <div className="sub-mgmt-stat">
                <label>Monthly txns</label>
                <strong>{formatPlanLimit(current.max_monthly_transactions)}</strong>
              </div>
              <div className="sub-mgmt-stat">
                <label>Employees</label>
                <strong>{formatPlanLimit(current.max_employees)}</strong>
              </div>
              <div className="sub-mgmt-stat">
                <label>Tanks</label>
                <strong>{formatPlanLimit(current.max_tanks)}</strong>
              </div>
            </div>
          )}
          {!overview?.is_active && (
            <button type="button" className="btn btn-light btn-sm mt-3 fw-semibold" onClick={() => switchTab('purchase')}>
              <i className="fa fa-shopping-cart me-1" /> Purchase or renew
            </button>
          )}
          {overview?.is_active && !gated && localStorage.getItem('STAT_OPERATION') !== 'Yes' && (
            <button
              type="button"
              className="btn btn-light btn-sm mt-3 fw-semibold"
              onClick={() => history.push('/fuel-config')}
            >
              Continue to pump setup
            </button>
          )}
        </div>

        {renderAutoBillingCard()}

        <h6 className="mb-3 fw-bold text-secondary">Subscription history</h6>
        <div className="sub-mgmt-table-wrap">
          <table className="table sub-mgmt-table mb-0">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Start</th>
                <th>End</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {historyRows.length === 0 && (
                <tr>
                  <td colSpan="5">
                    <div className="sub-mgmt-empty">
                      <div className="sub-mgmt-empty-icon"><i className="fa fa-history" /></div>
                      No subscription history yet
                    </div>
                  </td>
                </tr>
              )}
              {historyRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.plan_name}</td>
                  <td>{row.subscription_start_date}</td>
                  <td>{row.subscription_end_date}</td>
                  <td>{formatCurrency(row.amount)}</td>
                  <td><StatusBadge active={row.is_active} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  function renderPurchase() {
    return (
      <>
        {renderGatewayBanner()}
        <p className="text-muted mb-4">
          Select a plan. Paid packages can be prepaid for 1–36 months. The free trial has no payment
          and does not open Razorpay.
        </p>
        <div className="sub-mgmt-plan-grid">
          {plans.map((plan, index) => {
            const free = isFreePlan(plan);
            const trialUsed = Boolean(overview?.access?.trial_used);
            return (
            <div
              className={`sub-mgmt-plan-card ${index === 1 && !free ? 'is-featured' : ''}`}
              key={plan.id}
            >
              <div className="sub-mgmt-plan-head">
                {free && <span className="badge bg-success mb-1">Free trial</span>}
                {index === 1 && !free && <span className="badge bg-primary mb-1">Popular</span>}
                <div>{plan.plan_name}</div>
              </div>
              <div className="sub-mgmt-plan-body">
                <div className="sub-mgmt-plan-price">
                  {free ? '₹0' : formatCurrency(plan.monthly_amount || plan.amount)}
                  <small className="sub-mgmt-plan-price-unit">{free ? ` / ${plan.duration} days` : ' / month'}</small>
                </div>
                <p className="sub-mgmt-plan-desc">{plan.plan_desc || 'Full access to Petrosmart operational modules.'}</p>
                <PlanLimitsList plan={plan} />
                {!free && (plan.advance_offers || []).length > 0 && (
                  <ul className="sub-mgmt-offer-list">
                    {plan.advance_offers.map((offer) => (
                      <li key={offer.months}>
                        Pay {offer.months} months in advance · <strong>{offer.discount_percent}% off</strong>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="sub-mgmt-plan-meta">
                  <i className="fa fa-calendar me-1" />
                  {free ? `Valid for ${plan.duration} days · No payment` : 'Choose 1–36 months at checkout'}
                </p>
                <button
                  type="button"
                  className="sub-mgmt-btn-primary"
                  disabled={free && trialUsed}
                  onClick={() => openCheckout(plan)}
                >
                  <i className={`fa ${free ? 'fa-gift' : 'fa-bolt'} me-1`} />
                  {free ? (trialUsed ? 'Trial already used' : 'Start free trial') : 'Subscribe now'}
                </button>
              </div>
            </div>
            );
          })}
          {plans.length === 0 && (
            <div className="sub-mgmt-empty col-12">
              <div className="sub-mgmt-empty-icon"><i className="fa fa-inbox" /></div>
              No plans available. Contact support.
            </div>
          )}
        </div>
      </>
    );
  }

  function renderPayments() {
    return (
      <div className="sub-mgmt-panel">
        <div className="sub-mgmt-table-wrap">
          <table className="table sub-mgmt-table mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Order ID</th>
                <th>Plan</th>
                <th>Gateway</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Transaction</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr>
                  <td colSpan="7">
                    <div className="sub-mgmt-empty">
                      <div className="sub-mgmt-empty-icon"><i className="fa fa-credit-card" /></div>
                      No payments yet — complete a subscription purchase to see history here
                    </div>
                  </td>
                </tr>
              )}
              {payments.map((row) => (
                <tr key={row.id}>
                  <td>{row.created_at}</td>
                  <td><code className="small">{row.order_id}</code></td>
                  <td>{row.plan_name}</td>
                  <td className="text-uppercase">
                    {row.payment_gateway}
                    {row.is_auto_debit ? <span className="sub-mgmt-auto-mini">Auto debit</span> : null}
                  </td>
                  <td>{formatCurrency(row.amount)}</td>
                  <td className={paymentStatusClass(row.payment_status_label)}>
                    {row.payment_status_label}
                  </td>
                  <td>
                    {row.transaction_id || '—'}
                    {row.coupon_code ? <div className="small text-success">{row.coupon_code}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function renderGst() {
    return (
      <div className="sub-mgmt-panel">
        <GstDetailsForm
          values={gstForm}
          errors={gstErrors}
          saving={gstSaving}
          onChange={handleGstFieldChange}
          onSubmit={handleGstSubmit}
        />
      </div>
    );
  }

  function renderInvoices() {
    return (
      <div className="sub-mgmt-panel">
        <p className="text-muted small mb-3">
          Approved subscription invoices with payment receipts. Unpaid invoices can be paid online with Razorpay.
        </p>
        <div className="sub-mgmt-table-wrap">
          <table className="table sub-mgmt-table mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice #</th>
                <th>Description</th>
                <th>Total</th>
                <th>Received</th>
                <th>Pending</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 && (
                <tr>
                  <td colSpan="7">
                    <div className="sub-mgmt-empty">
                      <div className="sub-mgmt-empty-icon"><i className="fa fa-file-text-o" /></div>
                      No subscription invoices yet — they appear after a successful payment
                    </div>
                  </td>
                </tr>
              )}
              {invoices.map((row) => (
                <tr key={row.id}>
                  <td>{formatReportDate(row.op_date)}</td>
                  <td>{row.id}</td>
                  <td>{row.invoice_source_label || 'Subscription'}</td>
                  <td>{formatCurrency(row.total)}</td>
                  <td>{formatCurrency(row.amount_received)}</td>
                  <td className={(Number(row.amount_pending) || 0) > 0.01 ? 'text-danger fw-semibold' : ''}>
                    {formatCurrency(row.amount_pending)}
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-2 justify-content-end">
                      {(Number(row.amount_pending) || 0) > 0.01 && (
                        <button
                          type="button"
                          className="btn btn-warning btn-sm fw-semibold"
                          disabled={!!payingInvoiceId}
                          onClick={() => payPendingInvoice(row)}
                        >
                          <i className="fa fa-credit-card me-1" />
                          {payingInvoiceId === row.id
                            ? 'Opening Razorpay…'
                            : `Pay online (${formatCurrency(row.amount_pending)})`}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => downloadInvoice(row)}
                      >
                        <i className="fa fa-download me-1" /> PDF
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} />
        <div className="content-area">
          <ManagerLoginHeader props={props} page_title="Subscription Management" />
          {!permissions.includes('Subscription Management') && !isSubscriptionAccessBlocked() ? (
            <div className="form-wrap mt-5 text-center">You don&apos;t have permission to access this page</div>
          ) : (
            <div className="container pb-4 sub-mgmt-page">
              <div className="form-wrap mt-4">
                <ul className="nav sub-mgmt-tabs">
                  {TABS.map((tab) => (
                    <li className="nav-item" key={tab.id}>
                      <button
                        type="button"
                        className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => switchTab(tab.id)}
                      >
                        <i className={`fa ${tab.icon} me-1`} />
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>

                {loading ? <Loader /> : (
                  <>
                    {activeTab === 'overview' && renderOverview()}
                    {activeTab === 'purchase' && renderPurchase()}
                    {activeTab === 'payments' && renderPayments()}
                    {activeTab === 'invoices' && renderInvoices()}
                    {activeTab === 'gst' && renderGst()}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={checkoutOpen}
        onHide={() => !paying && setCheckoutOpen(false)}
        size="lg"
        className="sub-mgmt-checkout-modal"
        centered
      >
        <Modal.Header closeButton className="sub-mgmt-checkout-header">
          <div className="sub-mgmt-checkout-heading">
            <span className="sub-mgmt-checkout-kicker">Checkout</span>
            <Modal.Title>{selectedPlan?.plan_name || 'Plan'}</Modal.Title>
            <p className="sub-mgmt-checkout-sub">
              {isFreePlan(selectedPlan)
                ? 'Confirm your details. There is no payment for the free trial.'
                : 'Choose a billing period and complete a secure payment'}
            </p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="sub-mgmt-checkout-summary">
            {!isFreePlan(selectedPlan) && (
            <div className="mb-3">
              <label className="form-label fw-semibold mb-2">Billing period</label>
              <div className="sub-mgmt-month-pills">
                {BILLING_MONTH_OPTIONS.map((months) => {
                  const offer = (selectedPlan?.advance_offers || []).find((row) => Number(row.months) === months);
                  return (
                    <button
                      key={months}
                      type="button"
                      className={`sub-mgmt-month-pill ${Number(billingMonths) === months ? 'is-selected' : ''}`}
                      onClick={() => changeBillingMonths(months)}
                    >
                      {months} {months === 1 ? 'month' : 'months'}
                      {offer && !(appliedCoupon && couponMonthsOk) ? <span>{offer.discount_percent}% off</span> : null}
                    </button>
                  );
                })}
              </div>
              <div className="d-flex align-items-center gap-2 mt-2">
                <label className="small text-muted mb-0" htmlFor="billing-months-input">Custom months</label>
                <input
                  id="billing-months-input"
                  type="number"
                  min="1"
                  max="36"
                  className="form-control form-control-sm"
                  style={{ width: 90 }}
                  value={billingMonths}
                  onChange={(e) => changeBillingMonths(e.target.value)}
                />
              </div>
              <div className="sub-mgmt-coupon mt-3">
                <label className="form-label fw-semibold mb-2" htmlFor="checkout-coupon-input">Promo coupon</label>
                <div className="d-flex gap-2">
                  <input
                    id="checkout-coupon-input"
                    className="form-control text-uppercase"
                    value={couponInput}
                    onChange={(e) => setCouponInput(normalizeCouponCode(e.target.value))}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = e.clipboardData?.getData('text') || '';
                      setCouponInput(normalizeCouponCode(`${couponInput}${pasted}`));
                    }}
                    placeholder="Enter code"
                    disabled={paying || couponApplying}
                    maxLength={32}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {appliedCoupon ? (
                    <button type="button" className="btn btn-outline-secondary" disabled={paying} onClick={() => removeCheckoutCoupon()}>
                      Remove
                    </button>
                  ) : (
                    <button type="button" className="btn btn-outline-primary" disabled={paying || couponApplying} onClick={() => applyCheckoutCoupon()}>
                      {couponApplying ? 'Checking…' : 'Apply'}
                    </button>
                  )}
                </div>
                {couponMessage ? (
                  <div className={`small mt-2 ${appliedCoupon ? 'text-success' : 'text-danger'}`}>{couponMessage}</div>
                ) : null}
                {appliedCoupon && !couponMonthsOk ? (
                  <div className="small text-warning mt-1">
                    This coupon needs at least {appliedCoupon.min_months} month{Number(appliedCoupon.min_months) === 1 ? '' : 's'}. Plan advance offer is shown until then.
                  </div>
                ) : null}
                {appliedCoupon && couponMonthsOk ? (
                  <div className="small text-muted mt-1">
                    {Number(checkoutQuote?.coupon_discount_months || appliedCoupon.discount_months || appliedCoupon.min_months) < Number(billingMonths)
                      ? `${appliedCoupon.discount_percent}% off the first ${checkoutQuote?.coupon_discount_months || appliedCoupon.discount_months || appliedCoupon.min_months} months only; remaining months are full price. Replaces the plan advance offer.`
                      : 'Coupon replaces the plan advance offer for this payment.'}
                  </div>
                ) : null}
              </div>
            </div>
            )}
            {isFreePlan(selectedPlan) ? (
              <>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Free trial</span>
                  <strong>{selectedPlan?.duration || 0} days</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                  <span className="text-muted">Amount to pay</span>
                  <strong className="fs-5 text-success">₹0</strong>
                </div>
                <div className="small text-muted mt-1">No Razorpay redirect. Access starts immediately.</div>
              </>
            ) : (
              <>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Monthly rate</span>
              <strong>{formatCurrency(checkoutQuote?.monthly_amount)}</strong>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-1">
              <span className="text-muted">{checkoutQuote?.months || 1} month{(checkoutQuote?.months || 1) === 1 ? '' : 's'}</span>
              <strong>{formatCurrency(checkoutQuote?.gross_amount)}</strong>
            </div>
            {checkoutQuote?.discount_amount > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-1 text-success">
                <span>
                  {checkoutQuote.discount_source === 'coupon'
                    ? `Promo coupon ${checkoutQuote.coupon?.code || ''} (${checkoutQuote.discount_percent}% off first ${checkoutQuote.coupon_discount_months || checkoutQuote.coupon?.discount_months || checkoutQuote.coupon?.min_months || ''} mo)`
                    : `Advance offer (${checkoutQuote.discount_percent}% off)`}
                </span>
                <strong>-{formatCurrency(checkoutQuote.discount_amount)}</strong>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center mt-1">
              <span className="text-muted">GST ({checkoutQuote?.gst_percent || 0}%)</span>
              <strong>+{formatCurrency(checkoutQuote?.gst_amount)}</strong>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
              <span className="text-muted">Amount to pay (incl. GST)</span>
              <strong className="fs-5 text-primary">{formatCurrency(checkoutQuote?.payable_amount)}</strong>
            </div>
            <div className="small text-muted mt-1">
              Valid for {checkoutQuote?.months || 1} month{(checkoutQuote?.months || 1) === 1 ? '' : 's'} · Secure payment
            </div>
              </>
            )}
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Billing name <span className="text-danger">*</span></label>
              <input className="form-control" name="billing_name" value={billing.billing_name} onChange={handleBillingChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Email <span className="text-danger">*</span></label>
              <input type="email" className="form-control" name="billing_email" value={billing.billing_email} onChange={handleBillingChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Phone <span className="text-danger">*</span></label>
              <input className="form-control" name="billing_tel" value={billing.billing_tel} onChange={handleBillingChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">GSTIN</label>
              <input
                className="form-control text-uppercase"
                name="billing_gstin"
                maxLength={15}
                value={billing.billing_gstin}
                onChange={handleBillingChange}
                placeholder="15-character GSTIN"
                autoComplete="off"
              />
            </div>
            <div className="col-12">
              <GstLocationFields
                stateCode={billing.billing_state_code}
                district={billing.billing_district}
                onChange={handleCheckoutLocationChange}
                required={false}
              />
            </div>
            <div className="col-12">
              <label className="form-label fw-semibold">Address</label>
              <input className="form-control" name="billing_address" value={billing.billing_address} onChange={handleBillingChange} />
            </div>
            {!isFreePlan(selectedPlan) && (
            <div className="col-12">
              <label className="form-label fw-semibold">Payment gateway <span className="text-danger">*</span></label>
              <div className="sub-mgmt-gateway-pills">
                {gateways.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    className={`sub-mgmt-gateway-pill ${selectedGateway === g.id ? 'is-selected' : ''}`}
                    onClick={() => setSelectedGateway(g.id)}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
              {selectedGateway === 'razorpay' && (
                <label className="sub-mgmt-switch mt-3 mb-0">
                  <input
                    type="checkbox"
                    checked={autoDebitCheckout}
                    onChange={(e) => setAutoDebitCheckout(e.target.checked)}
                  />
                  <span>Enable auto debit &amp; auto renewal</span>
                  <small>Save this Razorpay method. When the next period is due, Petrosmart charges it and renews the account.</small>
                </label>
              )}
            </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" disabled={paying} onClick={() => setCheckoutOpen(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn-success sub-mgmt-pay-btn" disabled={paying} onClick={handlePay}>
            {paying ? (
              <><i className="fa fa-spinner fa-spin me-1" /> Processing…</>
            ) : isFreePlan(selectedPlan) ? (
              <><i className="fa fa-gift me-1" /> Start free trial</>
            ) : (
              <><i className="fa fa-lock me-1" /> Pay {formatCurrency(checkoutQuote?.payable_amount || billing.amount)}</>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SubscriptionManagementConfig;
